import React, {useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useHistory, useLocation, Link} from "react-router-dom";
import {getFlowFromURL, getParamFromURL, FLOWS, removeTrailingSlashes} from "src/utils/url_utils";
import {
    TableHeader,
    Table,
    TableHead,
    TableHeadRow,
    TableBody,
    TableCell,
    IconColumn,
    TableRowWithShadow,
    PaddedSpan,
    CreateNewButton
} from "../style/table_styles";
import {CouldNotLoadAlert} from "../style/common_styles";
import { GreenSpinner } from "../style/header_styles";
import {useEffect} from "react";
import {colorGreen, colorGrey, DeleteIconTable, EditIconTable, ViewIconTable} from "src/view/style/icons";
import * as configPanelActions from "../../control/actions/configPanel/config_panel_actions"
import * as featureGenerationActions from "../../control/actions/featureGeneration/namespace_actions"
import * as modelTrainingActions from "../../control/actions/modelTraining/namespace_actions"
import {ERROR_OPERATION_NOT_SUPPORTED, ERROR_TEAM_NOT_FOUND} from "src/control/errors";
import {formatDate, isEmpty, setIfMounted} from "src/utils/common_utils";
import * as configPanelSelectors from "src/control/selectors/configPanel/config_panel_selectors";
import * as featureGenerationSelectors from "src/control/selectors/featureGeneration/feature_generation_selectors";
import * as modelTrainingSelectors from "src/control/selectors/modelTraining/model_training_selectors";
import {showSnackBar} from "src/control/actions/commons/snack_bar_actions";
import ReactTooltip from "react-tooltip";
import {getUniqueItemsFromObjectList} from "src/utils/list_utils";


const ListNamespaces = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const teamId = getParamFromURL("teamId");
    const flow = getFlowFromURL(location.pathname);

    //Used to prevent crashes due to memory leak problem
    let _isMounted = useRef(true);

    const [namespaceLoaded, setNamespaceLoaded] = useState(false);
    const [errorOccurred, setErrorOccurred] = useState(false);

    const getSelectorMethod = (flow: any) => {
        switch (flow) {
            case FLOWS.CONFIG_PANEL: return configPanelSelectors.allNamespacesSelector;
            case FLOWS.FEATURE_GENERATION: return featureGenerationSelectors.allNamespacesSelector;
            case FLOWS.MODEL_TRAINING: return modelTrainingSelectors.allNamespacesSelector;
            default: return (state: any) => {};
        }
    }

    let namespaces = useSelector(getSelectorMethod(flow));

    useEffect(() => {
        _isMounted.current = true;
    });

    useEffect(() => {
        return () => {
            _isMounted.current = false;
        }
    });

    const getActionMethod = (flow: any) => {
        switch (flow) {
            case FLOWS.CONFIG_PANEL: return configPanelActions.fetchAllNamespaces;
            case FLOWS.MODEL_TRAINING: return modelTrainingActions.fetchAllNamespaces;
            case FLOWS.FEATURE_GENERATION: return featureGenerationActions.fetchAllNamespaces;
            default: return (teamId: any) => (dispatch: any) => {};
        }

    }

    const getCreateNewButton = (flow: any) => {
        return (
            <CreateNewButton
                variant={"primary"}
                onClick={handleCreateNew}
                disabled= {flow === FLOWS.CONFIG_PANEL}
            >
                Create New!
            </CreateNewButton>
        )
    }

    const initialize = async () => {
        try {
            setIfMounted(_isMounted.current, setNamespaceLoaded, false);
            if (isEmpty(teamId)) {
                throw ERROR_TEAM_NOT_FOUND;
            }
            const fetchAllNamespaces = getActionMethod(flow);
            await dispatch(fetchAllNamespaces(teamId));
            setIfMounted(_isMounted.current, setErrorOccurred, false);
        } catch (error) {
            setIfMounted(_isMounted.current, setErrorOccurred, true);
            dispatch(showSnackBar(error.message));
        }

    }

    const initializeStates = () => {
        if(!isEmpty(namespaces)) {
            let loadedTeamIds = getUniqueItemsFromObjectList(namespaces, "teamId")
            loadedTeamIds.length == 1
            && setIfMounted(_isMounted.current, setNamespaceLoaded, loadedTeamIds[0]==teamId)
        }
    }

    useEffect(  () => {
        if (_isMounted.current)
            initialize();
    }, [dispatch, teamId]);

    useEffect(  () => {
        if (_isMounted.current)
            initializeStates();
    }, [dispatch, namespaces]);

    const handleNavigation =  (namespaceId: string) => {
        let current_path = removeTrailingSlashes(location.pathname);
        let dest_path = current_path + "/" + namespaceId

        switch (flow) {
            case FLOWS.CONFIG_PANEL:
                dest_path = dest_path + "/configs"; break;
            case FLOWS.MODEL_TRAINING:
                dest_path = dest_path + "/runways"; break;
            case FLOWS.FEATURE_GENERATION:
                dest_path = dest_path + "/jobs"; break;
            default:
                dest_path = "/notFound"; break;
        }

        history.push(
            dest_path
        );
    }

    const handleCreateNew = () => {
        let current_path = removeTrailingSlashes(location.pathname);
        let dest_path = current_path + "/new";
        if (flow === FLOWS.CONFIG_PANEL) {
            return;
        }
        if(flow === FLOWS.MODEL_TRAINING) {
            dest_path = `${current_path}/create`;
        }
        history.push(dest_path);
    }

    const handleRedirectToViewNamespace = (namespaceId: string) => {
        let current_path = removeTrailingSlashes(location.pathname);
        let dest_path = current_path + "/" + namespaceId + "/view";
        switch (flow) {
            case FLOWS.CONFIG_PANEL:
                dispatch(showSnackBar(ERROR_OPERATION_NOT_SUPPORTED.message));
                return;
        }
        history.push(dest_path);

    }

    const handleRedirectToEditNamespace = (namespaceId: string) => {
        let current_path = removeTrailingSlashes(location.pathname);
        let dest_path = current_path + "/" + namespaceId + "/edit";
        switch (flow) {
            case FLOWS.CONFIG_PANEL:
            case FLOWS.FEATURE_GENERATION:
                dispatch(showSnackBar(ERROR_OPERATION_NOT_SUPPORTED.message));
                return;
        }
        history.push(dest_path);
    }

    const showNamespaces = () => {

        if (errorOccurred) {
            return (
                <CouldNotLoadAlert variant={"danger"} header={"Error"}>
                    Could Not Find Namespaces
                </CouldNotLoadAlert>
            )
        }

        if (!namespaceLoaded) {
            return (<GreenSpinner variant={"default"} size={"large"} />)
        }

        return namespaces.map((namespace: any, index: number)=> (
            <TableRowWithShadow key={index}>
                <TableCell onClick={() => handleNavigation(namespace.namespaceId)}>
                    {namespace.namespace}
                </TableCell>
                <TableCell onClick={() => handleNavigation(namespace.namespaceId)}>
                    {namespace.createdBy}
                </TableCell>
                <TableCell onClick={() => handleNavigation(namespace.namespaceId)}>
                    {formatDate(namespace.creationTime)}
                </TableCell>
                <TableCell onClick={() => handleNavigation(namespace.namespaceId)}>
                    {namespace.lastUpdatedBy}
                </TableCell>
                <TableCell onClick={() => handleNavigation(namespace.namespaceId)}>
                    {formatDate(namespace.lastUpdatedTime)}
                </TableCell>
                <TableCell>
                    <ReactTooltip place="bottom" type="dark"/>

                    <PaddedSpan data-tip="View Namespace Details" onClick={() => handleRedirectToViewNamespace(namespace.namespaceId)}>
                        <ViewIconTable style={flow === FLOWS.CONFIG_PANEL? colorGrey : colorGreen}/>
                    </PaddedSpan>

                    <PaddedSpan data-tip="Edit Namespace" onClick={() => handleRedirectToEditNamespace(namespace.namespaceId)}>
                        <EditIconTable style={flow === FLOWS.CONFIG_PANEL || flow === FLOWS.FEATURE_GENERATION ? colorGrey : colorGreen}/>
                    </PaddedSpan>

                    <PaddedSpan data-tip="Delete Namespace" onClick={() => {dispatch(showSnackBar(ERROR_OPERATION_NOT_SUPPORTED.message))}}>
                        <DeleteIconTable style={colorGrey}/>
                    </PaddedSpan>
                </TableCell>
            </TableRowWithShadow>
        ));

    }

    return (
        <>
            <div>
                <TableHeader> Namespaces
                    {getCreateNewButton(flow)}
                </TableHeader>
            </div>
            <Table>
                <TableHead>
                    <TableHeadRow>
                        <TableCell>
                            Name
                        </TableCell>
                        <TableCell>
                            Created By
                        </TableCell>
                        <TableCell>
                            Created At
                        </TableCell>
                        <TableCell>
                            Last Updated By
                        </TableCell>
                        <TableCell>
                            Last Updated At
                        </TableCell>
                        <TableCell>
                            Actions
                        </TableCell>
                    </TableHeadRow>
                </TableHead>
                <TableBody>
                    {showNamespaces()}
                </TableBody>
            </Table>

        </>
    )
}

export default ListNamespaces;
